import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  BarChart3,
  Filter,
  Search,
  Save,
  RotateCcw,
  Eye,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Zap,
  Target,
  Clock,
  FileText,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  Star,
  Crown,
  Shield,
  Activity,
  RefreshCw,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  Timer,
  Brain,
  Route,
  Zap as Lightning,
  Cpu,
  Database,
  Network,
  Monitor
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';

const AutoAssignmentDashboard = () => {
  const { getMockData, updateMockData } = useDataContext();
  const [assignments, setAssignments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');

  useEffect(() => {
    loadAutoAssignmentData();
  }, []);

  const loadAutoAssignmentData = async () => {
    try {
      const data = getMockData('autoAssignment');
      const agentsData = getMockData('agents');
      const ticketsData = getMockData('tickets');
      
      setAssignments(data?.assignments || []);
      setAgents(agentsData?.agents || []);
      setTickets(ticketsData?.tickets || []);
    } catch (error) {
      console.error('Failed to load auto-assignment data:', error);
    }
  };

  const handleCreateRule = () => {
    setIsCreatingRule(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this assignment rule?')) {
      const updatedAssignments = assignments.filter(assignment => assignment.id !== ruleId);
      setAssignments(updatedAssignments);
      updateMockData('autoAssignment', { assignments: updatedAssignments });
    }
  };

  const handleToggleRule = (ruleId) => {
    const updatedAssignments = assignments.map(assignment => 
      assignment.id === ruleId ? { ...assignment, isActive: !assignment.isActive } : assignment
    );
    setAssignments(updatedAssignments);
    updateMockData('autoAssignment', { assignments: updatedAssignments });
  };

  const handleManualOverride = (assignmentId) => {
    alert(`Manual override for assignment ${assignmentId} - reassigning to different agent...`);
  };

  const handleOptimizeAssignment = () => {
    alert('Running assignment optimization algorithm...');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned': return 'green';
      case 'pending': return 'yellow';
      case 'failed': return 'red';
      case 'overridden': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'assigned': return CheckCircle;
      case 'pending': return Clock;
      case 'failed': return AlertTriangle;
      case 'overridden': return UserCheck;
      default: return AlertTriangle;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.ticketTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.assignedAgent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || assignment.status === filterStatus;
    const matchesAgent = filterAgent === 'all' || assignment.assignedAgent === filterAgent;
    return matchesSearch && matchesStatus && matchesAgent;
  });

  const mockAssignments = [
    {
      id: 1,
      ticketId: "TKT-2025-001",
      ticketTitle: "Login Issues with Mobile App",
      category: "Technical",
      priority: "high",
      status: "assigned",
      assignedAgent: "Sarah Johnson",
      assignmentMethod: "AI Algorithm",
      assignmentTime: "0.3s",
      confidence: 95.2,
      assignedAt: "2025-01-15T10:30:00Z",
      workloadBalance: 75,
      skillMatch: 98,
      responseTime: "2.5 hours"
    },
    {
      id: 2,
      ticketId: "TKT-2025-002",
      ticketTitle: "Billing Question - Invoice Discrepancy",
      category: "Billing",
      priority: "medium",
      status: "assigned",
      assignedAgent: "David Wilson",
      assignmentMethod: "Rule-based",
      assignmentTime: "0.1s",
      confidence: 88.7,
      assignedAt: "2025-01-15T10:25:00Z",
      workloadBalance: 60,
      skillMatch: 92,
      responseTime: "1.8 hours"
    },
    {
      id: 3,
      ticketId: "TKT-2025-003",
      ticketTitle: "Feature Request - Dark Mode",
      category: "Feature Request",
      priority: "low",
      status: "pending",
      assignedAgent: null,
      assignmentMethod: "Manual Review Required",
      assignmentTime: null,
      confidence: 45.3,
      assignedAt: "2025-01-15T10:20:00Z",
      workloadBalance: null,
      skillMatch: null,
      responseTime: null
    },
    {
      id: 4,
      ticketId: "TKT-2025-004",
      ticketTitle: "Integration Error - API Timeout",
      category: "Integration",
      priority: "high",
      status: "failed",
      assignedAgent: null,
      assignmentMethod: "AI Algorithm",
      assignmentTime: "0.5s",
      confidence: 23.1,
      assignedAt: "2025-01-15T10:15:00Z",
      workloadBalance: null,
      skillMatch: null,
      responseTime: null
    },
    {
      id: 5,
      ticketId: "TKT-2025-005",
      ticketTitle: "Account Setup Assistance",
      category: "Onboarding",
      priority: "medium",
      status: "overridden",
      assignedAgent: "Mike Chen",
      assignmentMethod: "Manual Override",
      assignmentTime: "0.2s",
      confidence: 67.8,
      assignedAt: "2025-01-15T10:10:00Z",
      workloadBalance: 85,
      skillMatch: 78,
      responseTime: "3.2 hours"
    }
  ];

  const assignmentMetrics = [
    {
      title: 'Total Assignments Today',
      value: assignments.length,
      icon: Users,
      color: 'blue',
      trend: '+12',
      trendDirection: 'up'
    },
    {
      title: 'Success Rate',
      value: '87.3%',
      icon: Target,
      color: 'green',
      trend: '+2.1%',
      trendDirection: 'up'
    },
    {
      title: 'Avg Assignment Time',
      value: '0.3s',
      icon: Timer,
      color: 'blue',
      trend: '-0.1s',
      trendDirection: 'down'
    },
    {
      title: 'Manual Overrides',
      value: assignments.filter(a => a.status === 'overridden').length,
      icon: UserCheck,
      color: 'yellow',
      trend: '+1',
      trendDirection: 'up'
    }
  ];

  const agentWorkload = [
    {
      name: "Sarah Johnson",
      currentTickets: 8,
      maxCapacity: 12,
      workloadPercentage: 66.7,
      avgResponseTime: "2.1 hours",
      skillLevel: "Expert",
      status: "online"
    },
    {
      name: "David Wilson",
      currentTickets: 5,
      maxCapacity: 10,
      workloadPercentage: 50.0,
      avgResponseTime: "1.8 hours",
      skillLevel: "Senior",
      status: "online"
    },
    {
      name: "Mike Chen",
      currentTickets: 11,
      maxCapacity: 12,
      workloadPercentage: 91.7,
      avgResponseTime: "3.2 hours",
      skillLevel: "Intermediate",
      status: "busy"
    },
    {
      name: "Lisa Rodriguez",
      currentTickets: 3,
      maxCapacity: 8,
      workloadPercentage: 37.5,
      avgResponseTime: "1.5 hours",
      skillLevel: "Expert",
      status: "online"
    }
  ];

  return (
    <div className="auto-assignment-dashboard p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Auto-Assignment Dashboard</h1>
            <p className="text-gray-600">Monitor automated ticket assignment performance and efficiency</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button onClick={handleCreateRule}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
            <Button variant="outline" onClick={handleOptimizeAssignment}>
              <Brain className="h-4 w-4 mr-2" />
              Optimize
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {assignmentMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">{metric.title}</CardTitle>
                <Icon className={`h-4 w-4 text-${metric.color}-500`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                <div className={`flex items-center text-xs ${
                  metric.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  {metric.trend} from yesterday
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Agent Workload Overview */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Agent Workload Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agentWorkload.map((agent, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">{agent.name}</h3>
                  <Badge variant={agent.status === 'online' ? 'default' : 'secondary'}>
                    {agent.status}
                  </Badge>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{agent.currentTickets} / {agent.maxCapacity} tickets</span>
                    <span>{agent.workloadPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        agent.workloadPercentage > 80 ? 'bg-red-500' : 
                        agent.workloadPercentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${agent.workloadPercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <div>Avg Response: {agent.avgResponseTime}</div>
                  <div>Skill Level: {agent.skillLevel}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter Assignments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search assignments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="overridden">Overridden</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAgent} onValueChange={setFilterAgent}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                <SelectItem value="Sarah Johnson">Sarah Johnson</SelectItem>
                <SelectItem value="David Wilson">David Wilson</SelectItem>
                <SelectItem value="Mike Chen">Mike Chen</SelectItem>
                <SelectItem value="Lisa Rodriguez">Lisa Rodriguez</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assignments List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Route className="h-5 w-5 mr-2" />
            Assignment History ({mockAssignments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mockAssignments.map((assignment) => {
              const StatusIcon = getStatusIcon(assignment.status);
              return (
                <div key={assignment.id} className="p-6 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{assignment.ticketTitle}</h3>
                        <Badge variant={assignment.status === 'assigned' ? 'default' : 'secondary'}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {assignment.status}
                        </Badge>
                        <Badge variant={getPriorityColor(assignment.priority) === 'red' ? 'destructive' : 'default'}>
                          {assignment.priority} priority
                        </Badge>
                        <Badge variant="outline">{assignment.category}</Badge>
                      </div>
                      <p className="text-gray-600 mb-3">Ticket ID: {assignment.ticketId}</p>
                      
                      {/* Assignment Details */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Assignment Details:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Agent:</span> {assignment.assignedAgent || 'Not assigned'}
                          </div>
                          <div>
                            <span className="font-medium">Method:</span> {assignment.assignmentMethod}
                          </div>
                          <div>
                            <span className="font-medium">Time:</span> {assignment.assignmentTime || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Confidence:</span> {assignment.confidence}%
                          </div>
                        </div>
                      </div>

                      {/* Performance Metrics */}
                      {assignment.status === 'assigned' && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Performance Metrics:</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Workload Balance:</span> {assignment.workloadBalance}%
                            </div>
                            <div>
                              <span className="font-medium">Skill Match:</span> {assignment.skillMatch}%
                            </div>
                            <div>
                              <span className="font-medium">Response Time:</span> {assignment.responseTime}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Timestamps */}
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>Assigned: {new Date(assignment.assignedAt).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {assignment.status === 'assigned' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleManualOverride(assignment.id)}
                        >
                          <UserCheck className="h-3 w-3 mr-1" />
                          Override
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditRule(assignment)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleRule(assignment.id)}
                      >
                        {assignment.isActive ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Disable
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Enable
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteRule(assignment.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button className="h-20 flex flex-col items-center justify-center space-y-2">
              <Plus className="h-6 w-6" />
              <span>Create Rule</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <Brain className="h-6 w-6" />
              <span>Optimize Algorithm</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <BarChart3 className="h-6 w-6" />
              <span>View Analytics</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <Settings className="h-6 w-6" />
              <span>Configure Rules</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoAssignmentDashboard;
